// Leaf blower: a ~60° cone in the facing direction pushes loose leaves.
// A garden vac (a blower with `collector`) runs the same cone the other way:
// it pulls loose leaves in over its short reach and bags them at the nozzle.

import type { Ctx } from '../ctx';
import { cellAt, T_BED } from '../layout';

export const BLOW_HALF_ANGLE = Math.PI / 6;
export const BLOW_ACCEL = 25;
export const BLOW_MAX_SPEED = 5;
/** Garden vac: a leaf this close to the operator (m, × √gameScale) goes in the bag. */
export const VAC_BAG_R = 0.45;

export function stepBlower(ctx: Ctx, dt: number): void {
  const slot = ctx.tools.blower;
  if (!slot) return;
  if (slot.model.collector) { stepVac(ctx, dt, ((slot.model.deckCm ?? 100) / 100) * Math.sqrt(ctx.gs)); return; }
  const sg = Math.sqrt(ctx.gs);
  const len = ((slot.model.deckCm ?? 250) / 100) * sg;
  const cosHalf = Math.cos(BLOW_HALF_ANGLE);
  const op = ctx.op;
  const fx = Math.cos(op.heading);
  const fy = Math.sin(op.heading);
  const accel = BLOW_ACCEL * sg;
  const vmax = BLOW_MAX_SPEED * sg;
  const L = ctx.leaves;
  const g = ctx.grid;
  for (let k = 0; k < L.count; k++) {
    if (L.state[k] !== 0) continue;
    const dx = L.x[k] - op.x;
    const dy = L.y[k] - op.y;
    if (dx > len || dx < -len || dy > len || dy < -len) continue;
    const d2 = dx * dx + dy * dy;
    if (d2 > len * len) continue;
    const d = Math.sqrt(d2);
    let dirx = fx;
    let diry = fy;
    if (d > 1e-3) {
      const rx = dx / d;
      const ry = dy / d;
      if (rx * fx + ry * fy < cosHalf) continue;
      dirx = fx * 0.75 + rx * 0.25;
      diry = fy * 0.75 + ry * 0.25;
    }
    const ci = cellAt(g, L.x[k], L.y[k]);
    const bed = ci >= 0 && g.type[ci] === T_BED ? 0.25 : 1;
    const a = accel * (1 - d / len) * bed * dt;
    let vx = L.vx[k] + dirx * a;
    let vy = L.vy[k] + diry * a;
    const sp = Math.hypot(vx, vy);
    if (sp > vmax) {
      vx *= vmax / sp;
      vy *= vmax / sp;
    }
    L.vx[k] = vx;
    L.vy[k] = vy;
    // Feature P6: a blown leaf isn't being raked any more (so a bed holds it).
    if (ctx.debris) ctx.leafRaked[k] = 0;
  }
  // Feature P9: the cone blows thrown clippings off the paving.
  if (ctx.blowBack && ctx.clipList.length) clearClippings(ctx, fx, fy, len, cosHalf);
}

/**
 * Garden vac: loose leaves in the cone are pulled towards the operator and
 * bagged (LEAF_BAGGED) once within VAC_BAG_R. A bagged leaf is off the paving
 * (a clear counts it) and in the bag (a collect counts it). With debris rules
 * a bed's leaves stay the rake's job, as with a mower's catcher.
 */
function stepVac(ctx: Ctx, dt: number, len: number): void {
  const sg = Math.sqrt(ctx.gs);
  const cosHalf = Math.cos(BLOW_HALF_ANGLE);
  const op = ctx.op;
  const fx = Math.cos(op.heading);
  const fy = Math.sin(op.heading);
  const bagR = VAC_BAG_R * sg;
  const accel = BLOW_ACCEL * sg;
  const vmax = BLOW_MAX_SPEED * sg;
  const L = ctx.leaves;
  const g = ctx.grid;
  let bagged = 0;
  for (let k = 0; k < L.count; k++) {
    if (L.state[k] !== 0) continue;
    const dx = L.x[k] - op.x;
    const dy = L.y[k] - op.y;
    if (dx > len || dx < -len || dy > len || dy < -len) continue;
    const d2 = dx * dx + dy * dy;
    if (d2 > len * len) continue;
    const d = Math.sqrt(d2);
    if (d > 1e-3 && (dx * fx + dy * fy) / d < cosHalf && d > bagR) continue;
    if (ctx.debris) {
      const ci = cellAt(g, L.x[k], L.y[k]);
      if (ci >= 0 && g.type[ci] === T_BED) continue;
    }
    if (d <= bagR) {
      L.state[k] = 2;
      L.vx[k] = 0;
      L.vy[k] = 0;
      if (ctx.debris) ctx.leafCaught[k] = 1;
      bagged++;
      continue;
    }
    // Pull towards the nozzle, harder the closer it gets.
    const a = accel * (1 - d / len) * dt;
    let vx = L.vx[k] - (dx / d) * a;
    let vy = L.vy[k] - (dy / d) * a;
    const sp = Math.hypot(vx, vy);
    if (sp > vmax) {
      vx *= vmax / sp;
      vy *= vmax / sp;
    }
    L.vx[k] = vx;
    L.vy[k] = vy;
    if (ctx.debris) ctx.leafRaked[k] = 0;
  }
  if (bagged) ctx.collectorCount += bagged;
  // Feature P9: it sucks thrown clippings off the paving too.
  if (ctx.blowBack && ctx.clipList.length) clearClippings(ctx, fx, fy, len, cosHalf);
}

function clearClippings(ctx: Ctx, fx: number, fy: number, len: number, cosHalf: number): void {
  const g = ctx.grid;
  const clip = g.clip;
  if (!clip) return;
  const op = ctx.op;
  const list = ctx.clipList;
  for (let k = list.length - 1; k >= 0; k--) {
    const i = list[k];
    const dx = ((i % g.w) + 0.5) * g.cellM - op.x;
    const dy = (Math.floor(i / g.w) + 0.5) * g.cellM - op.y;
    const d = Math.hypot(dx, dy);
    if (d > len + g.cellM * 0.5) continue;
    if (d > g.cellM && (dx * fx + dy * fy) / d < cosHalf) continue;
    clip[i] = 0;
    g.dirty.push(i);
    list[k] = list[list.length - 1];
    list.pop();
  }
}
